import { useState } from 'react'
import type { ListItem } from '@domain/list'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

type Props = {
  item: ListItem
  /** Quantos pendentes existem — o teto do campo "posição". */
  total: number
  /** A posição atual, contando de 1, para o campo já vir preenchido. */
  posicaoAtual: number
  onMover: (indiceZeroBased: number) => void
  onRemover: () => void
  onFechar: () => void
}

/**
 * As ações de um item que não cabem na linha: remover, topo, fundo, e uma
 * posição escolhida.
 *
 * ## Ele abre por TRÊS portas, e isso é o requisito de acessibilidade
 *
 * Long-press na linha, toque na alça, e Enter na alça. As setas ↑↓ saíram da
 * linha quando o arrastar entrou; se este menu só abrisse por gesto de
 * ponteiro, quem usa teclado ou leitor de tela perderia o reordenar inteiro —
 * arrastar já é inacessível por natureza. A alça ser um `<button>` de verdade é
 * o que mantém esse caminho vivo.
 *
 * ## A folha em si é `FolhaDeBaixo`
 *
 * Fundo, Escape, foco e rolagem moram lá desde a decisão 230, quando o cadastro
 * de produto passou a precisar da mesma coisa. Aqui fica só o conteúdo.
 */
export function MenuDoItem({
  item,
  total,
  posicaoAtual,
  onMover,
  onRemover,
  onFechar,
}: Props) {
  const [posicao, setPosicao] = useState(String(posicaoAtual))

  const alvo = Number(posicao)
  const posicaoValida =
    posicao.trim() !== '' && Number.isInteger(alvo) && alvo >= 1 && alvo <= total

  return (
    <FolhaDeBaixo titulo={`Ações de ${item.text}`} onFechar={onFechar}>
        <p className="mb-2 truncate px-1 text-sm font-medium">{item.text}</p>

        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => {
              onMover(0)
            }}
            className="alvo justify-start rounded-lg px-3 text-left text-sm"
          >
            Mover para o topo
          </button>
          <button
            type="button"
            onClick={() => {
              onMover(total - 1)
            }}
            className="alvo justify-start rounded-lg px-3 text-left text-sm"
          >
            Mover para o fundo
          </button>

          <form
            className="flex items-center gap-2 px-3 py-1"
            onSubmit={(e) => {
              e.preventDefault()
              if (posicaoValida) onMover(alvo - 1)
            }}
          >
            <label htmlFor="posicao-do-item" className="text-sm">
              Mover para a posição
            </label>
            {/* Conta de 1 porque é o que a pessoa vê na tela; o domínio conta
                de 0, e a conversão acontece aqui, num lugar só. */}
            <input
              id="posicao-do-item"
              type="number"
              min={1}
              max={total}
              value={posicao}
              onChange={(e) => {
                setPosicao(e.target.value)
              }}
              className="w-16 rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-sm tabular-nums"
            />
            <button
              type="submit"
              disabled={!posicaoValida}
              className="alvo rounded-lg border border-[var(--border)] px-3 text-sm disabled:opacity-40"
            >
              Ir
            </button>
          </form>

          {/* Separado do resto por borda: é o único que não dá para desfazer,
              e `removeItem` apaga o documento de verdade. */}
          <button
            type="button"
            onClick={onRemover}
            className="alvo mt-1 justify-start rounded-lg border-t border-[var(--border)] px-3 text-left text-sm text-[var(--negative)]"
          >
            Remover da lista
          </button>

          <button
            type="button"
            onClick={onFechar}
            className="alvo justify-start rounded-lg px-3 text-left text-sm text-[var(--text-muted)]"
          >
            Fechar
          </button>
        </div>
    </FolhaDeBaixo>
  )
}
